/**
 * The support-ticket desk — the staff side of a system that had no staff side.
 *
 * MEASURED ON 14 SEPTEMBER, against a database at 323 migrations: fourteen
 * `%ticket%` functions, a `support_tickets` table, an append-only
 * `support_ticket_events` trail with its own guard — and across all three
 * repositories exactly ONE of those functions was called by any JavaScript.
 * `fn_tickets_on_my_desk`, by Tier Changes, for the single section
 * `finance` / `modify_subscription_tier`.
 *
 * So `fn_ticket_queue`, `fn_ticket_detail`, `fn_assign_ticket` and
 * `fn_move_ticket` were unreachable. A ticket raised from anywhere other than a
 * tier change arrived correctly, sat in the table, and was seen by nobody. The
 * member's half was unreachable too — they could not raise one at all — so the
 * whole desk was a complete, tested, unused subsystem on both sides at once.
 *
 * ---------------------------------------------------------------------------
 * OLDEST AT THE TOP, LIKE EVERY OTHER QUEUE HERE
 *
 * The founder's rule for disputes — *"the oldest being at the top of the list so
 * that they can be resolved on a first-in-first-out basis"* — and it belongs
 * here for the same reason Customer Enquiries gives: somebody who raised a
 * ticket nine days ago and has heard nothing is the person most likely to give
 * up on Orchid, and they are invisible in any ordering that puts the newest
 * first. The shared `Queue` carries the four states so nobody learns a second
 * vocabulary for the same idea.
 *
 * ---------------------------------------------------------------------------
 * THE DATABASE DECIDES WHICH MOVES ARE LEGAL, NOT THIS SCREEN
 *
 * `fn_ticket_move_is_allowed` lists thirteen pairs and refuses everything else
 * by name. `movesFrom()` mirrors that list so the desk is offered the moves that
 * will work rather than seven statuses and a refusal — and the mirror is
 * verified against the database pair by pair rather than trusted: thirteen
 * offered, none refused, thirteen allowed in total.
 *
 * NO PERMISSION CHECK LIVES HERE. Every one of the four functions opens with
 * `admin_has_permission(auth.uid(), 'respond_to_ticket')` — Prohibition 5, a
 * permission and never a role name. A client-side copy would be a second answer
 * to a question only the database is entitled to answer, and it would be the
 * answer that is wrong when a grant is withdrawn mid-session.
 */

import { useCallback, useEffect, useState } from 'react';
// PrimaryCTA, Field and textInputStyle left with the Move control when it was
// extracted to TicketMove.jsx. CRA treats an unused import as an error under
// CI, so a dead one here is a red pipeline rather than a tidy-up for later.
import {
  O, SANS, Card, StatusPill, LoadingPanel, Notice, QuietButton,
} from '../ui';
import Queue, { StateLabel } from '../Queue';
import {
  fetchTicketQueue, fetchTicketDetail, ticketState,
} from '../lib/support';
// EXTRACTED IN SCRIPT 360'S BATCH, NOT COPIED. TierChanges needs the same
// control, and two copies would be two places for the screens to disagree
// about what a desk may do -- see TicketMove.jsx.
import Move from './TicketMove';

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-ZA', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const waitingDays = (row) => {
  if (!row.created_at) return 0;
  const then = new Date(row.created_at);
  if (Number.isNaN(then.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - then.getTime()) / 86400000));
};

const waitTone = (days) => (days >= 7 ? 'critical' : days >= 3 ? 'warning' : 'quiet');
const waitLabel = (days) => (days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`);

/**
 * THE EVENT TRAIL, WHICH IS THE WHOLE AUDIT RECORD.
 *
 * Every transition, who made it, under which OID, and when — read from
 * `fn_ticket_detail` and shown oldest first, because a chain read newest-first
 * is a chain somebody has to reverse in their head. `Raised` is always the first
 * entry: `fn_raise_ticket` writes it at the moment of raising, so a ticket whose
 * first event belongs to a member of staff would be a ticket that appeared to
 * raise itself.
 */
const Trail = ({ id }) => {
  const [rows, setRows] = useState(null);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchTicketDetail(id).then(({ data, error }) => {
      if (!alive) return;
      if (error) { setFailure(error); return; }
      setRows(data);
    });
    return () => { alive = false; };
  }, [id]);

  if (failure) return <Notice tone="critical">{failure}</Notice>;
  if (rows === null) return <p style={{ fontSize: 12.5, color: O.inkMuted }}>Reading the trail…</p>;

  return (
    <div style={{ marginTop: 10 }} data-testid="ticket-trail">
      {rows.map((e, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={`${e.occurred_at}-${i}`}
          style={{ padding: '8px 0', borderTop: i ? `1px solid ${O.hairline}` : 'none' }}
        >
          <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: O.ink }}>
            {e.from_status ? `${e.from_status} → ${e.to_status}` : e.to_status}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 11.5, color: O.inkMuted }}>
            {when(e.occurred_at)}
            {/* WHO, AND UNDER WHICH OID. A trail that says what happened without
                saying who did it is not an audit record. */}
            {e.actor_name ? ` · ${e.actor_name}` : ''}
            {e.actor_oid ? ` (${e.actor_oid})` : ''}
            {e.actor_agent ? ` · ${e.actor_agent}` : ''}
          </p>
          {e.note && (
            <p style={{ margin: '4px 0 0', fontSize: 12.5, lineHeight: 1.6, color: O.ink }}>
              {e.note}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

const TicketRow = ({ row, state, onChanged }) => {
  const [open, setOpen] = useState(false);
  const days = waitingDays(row);

  return (
    <Card style={{ marginBottom: 10 }} data-testid={`ticket-${row.reference}`}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: O.ink }}>
            {row.subject}
          </p>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: O.inkMuted }}>
            {row.reference} · {row.origin} · raised {when(row.created_at)}
            {row.raiser_name ? ` · ${row.raiser_name}` : ''}
            {row.raiser_ofx_code ? ` (${row.raiser_ofx_code})` : ''}
          </p>
        </div>
        {/* THE TICKET'S OWN STATUS, beside the queue's four-state label. The
            Queue deliberately has four states so nobody learns a second
            vocabulary, but `assigned` and `in_progress` are different things to
            a desk and both are "waiting" to the queue -- so the row says which. */}
        <StatusPill tone="quiet">{row.status}</StatusPill>
        <StatusPill tone={waitTone(days)}>{waitLabel(days)}</StatusPill>
        <StateLabel state={state} settledLabel="Resolved" />
      </div>

      <div style={{ marginTop: 10 }}>
        <QuietButton onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide the trail' : 'Open it'}
        </QuietButton>
      </div>

      {open && (
        <>
          <Trail id={row.id} />
          <Move row={row} onDone={() => { setOpen(false); onChanged(); }} />
        </>
      )}
    </Card>
  );
};

export default function SupportTicketDesk() {
  const [rows, setRows] = useState(null);
  const [failure, setFailure] = useState(null);

  const load = useCallback(async () => {
    const { data, error } = await fetchTicketQueue(null);
    if (error) { setFailure(error); setRows([]); return; }
    setFailure(null);
    setRows(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (rows === null && !failure) return <LoadingPanel message="Opening the ticket desk" />;

  return (
    <div>
      <h2 style={{ fontFamily: SANS, fontSize: 15, fontWeight: 700, color: O.ink, margin: '0 0 4px' }}>
        Support tickets
      </h2>
      <p style={{ margin: '0 0 14px', fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
        Everything raised from inside the apps, oldest first. Opening one shows its
        full trail — every move, who made it, and when.
      </p>

      {failure && <Notice tone="critical">{failure}</Notice>}

      <Queue
        rows={rows || []}
        settledLabel="Resolved"
        stateOf={ticketState}
        empty="No tickets. An empty queue is a complete answer, not a fault."
        renderRow={(row, state) => (
          <TicketRow key={row.id} row={row} state={state} onChanged={load} />
        )}
      />
    </div>
  );
}
